import React, { useState, useEffect, useMemo } from 'react';
import {
  ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon,
  XMarkIcon, PencilIcon, PlusIcon, DocumentTextIcon, ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { SERVER_URL } from "../config";
import axios from 'axios';
import TableHeaderFilter from './TableHeaderfilter';
import SearchableDropdown from './SearchableDropdown';
import MultiSelectDropdown from './MultiSelectDropdown';
import FollowUpModal from "./FollowUpModal";
import QuotationDocumentPreview from './QuotationDocumentPreview';
import { buildQuotationPDF } from './PdfQuotation';

/* ── shared input style ─────────────────────────────────────────────────── */
const inputStyle =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm " +
  "text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 " +
  "focus:border-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-100";

/* ── status options ─────────────────────────────────────────────────────── */
const STATUS_OPTIONS = [
  'New Lead',
  'Estimate Required',
  'Estimate Submitted',
  'Follow up Running',
  'Negotiation',
  'Order Confirmed',
  'Completed & Review Collected',
  'Not Interested',
  'Lost',
];

/* Statuses that count as "closing" a lead — used to also close out any
   linked follow-up row. */
const CLOSING_STATUSES = ['completed & review collected', 'not interested', 'lost'];

/* ── reference source options (dropdown) ───────────────────────────────── */
const REFERENCE_OPTIONS = [
  'By Call',
  'Showroom',
  'Social Media',
  'Website',
  'Walk-in',
  'Email',
  'Reference',
  'Exhibition',
  'Other',
];

/* ════════════════════════════════════════════════════════════════════════ */
const LeadTable = ({
  leads = [],
  currentPage = 1,
  limit = 10,
  onPageChange = () => { },
  onLimitChange = () => { },
  searchQuery = '',
  onFilteredDataChange = () => { },
}) => {
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [newLeadModal, setNewLeadModal] = useState(false);
  const [viewLeadModal, setViewLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [errors, setErrors] = useState({});

  /* call-number prompt, shown inline when a lead is being moved to
     "Order Confirmed" but doesn't have a quotation number yet */
  const [callNumberInput, setCallNumberInput] = useState("");

  /* quotation preview/download modal — branch + customer come from
     whichever lead the user clicked "View Quotation" on, items + terms
     are fetched from the backend by quotation_number. */
  const [quotationPreview, setQuotationPreview] = useState({
    open: false, loading: false, error: "",
    branch: "", customer: null, quotationNumber: "",
    items: [], terms: [], createdBy: "",
  });

  /* options fetched from backend */
  const [allProducts, setAllProducts] = useState([]); // [{id,name,category,status,price}]
  const [branchOptions, setBranchOptions] = useState([]);

  /* table column filters */
  const [employeeNameFilter, setEmployeeNameFilter] = useState([]);
  const [leadNumberFilter, setLeadNumberFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [slNoFilter, setSlNoFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState([]);
  const [branchFilter, setBranchFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  const employee = JSON.parse(localStorage.getItem("employee")) || {
    employee_name: "",
    employee_id: ""
  };

  /* ── blank lead — qty / commission_amount / closedAmount removed;
     productNames replaces a single free product field ──────────────── */
  const blankLead = {
    slNo: '',
    companyName: '',
    category: '',
    productNames: [],
    contactPerson: '',
    mobileNumber: '',
    alternateMobileNumber: '',
    branch: '',
    mailId: '',
    address: '',
    location: '',
    pinCode: '',
    quotationNo: '',
    reference: '',
    referenceName: '',
    leadCreatedDate: '',
    nextFollowUp: '',
    status: 'New Lead',
    remarks: '',
  };
  const [newLead, setNewLead] = useState({ ...blankLead });

  /* ════════════════════════════════════════════════════════════════════
     FORM FIELDS
     Removed: usertype, rate, licenseType, qty, commission_amount, closedAmount
     Added: productNames (multi-select, filtered by category) right after category
  ════════════════════════════════════════════════════════════════════ */
  const formFields = [
    {
      name: 'slNo', label: 'Sl. No. (Tally)', type: 'text',
      placeholder: 'Tally Serial Number (optional)',
    },
    {
      name: 'companyName', label: 'Company Name', type: 'text',
      placeholder: 'Company Name', required: true, nonEditable: true,
    },
    {
      name: 'category', label: 'Category', type: 'select',
      placeholder: 'Select Category', options: 'categories', required: true,
    },
    {
      name: 'productNames', label: 'Product Description', type: 'multiselect',
      placeholder: 'Select Product(s)', required: true,
    },
    {
      name: 'contactPerson', label: 'Contact Person', type: 'text',
      placeholder: 'Contact Person Name & Designation',
    },
    {
      name: 'mobileNumber', label: 'Contact No.', type: 'tel',
      placeholder: 'Phone No.', required: true,
    },
    {
      name: 'alternateMobileNumber', label: 'Alternate Contact No.', type: 'tel',
      placeholder: 'Alternate Number',
    },
    {
      name: 'mailId', label: 'Email ID', type: 'email',
      placeholder: 'E-mail Id',
    },
    {
      name: 'address', label: 'Address', type: 'text',
      placeholder: 'Address', required: true,
    },
    {
      name: 'location', label: 'Location', type: 'text',
      placeholder: 'Location / Area',
    },
    {
      name: 'pinCode', label: 'Pincode', type: 'text',
      placeholder: 'Pincode',
    },
    {
      name: 'branch', label: 'Branch', type: 'select',
      placeholder: 'Select Branch', options: 'branches', required: true,
    },
    {
      name: 'reference', label: 'Reference', type: 'select',
      placeholder: 'Select Reference Source', options: 'referenceOpts',
    },
    {
      name: 'referenceName', label: 'Reference Name', type: 'text',
      placeholder: 'Reference Person Name',
    },
    {
      name: 'leadCreatedDate', label: 'Lead Creation Date', type: 'date',
      placeholder: '', nonEditable: true,
    },
    {
      name: 'nextFollowUp', label: 'Next Follow Up', type: 'datetime-local',
      placeholder: '', nonEditable: true,
    },
    {
      name: 'status', label: 'Status', type: 'select',
      placeholder: 'Select Status', options: 'statusOptions', required: true,
    },
    {
      name: 'remarks', label: 'Remarks', type: 'textarea',
      placeholder: 'Remarks / Follow-up notes', colSpan: 2,
    },
  ];

  /* ════════ FETCH ════════ */
  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/Rahul/fetchProductAndCategory.php`);
      if (res.data.success) {
        const list = (res.data.products || []).map(item => ({
          id: item.id,
          name: item.name,
          category: item.service, // backend returns category under "service"
          status: item.status,
          price: item.price,
        })).filter(p => p.name);
        setAllProducts(list);
      }
    } catch (error) {
      console.error('productDetailsFetch error', error);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/branchFetch.php`, {
        employee_id: employee.employee_id,
      });
      if (res.data.status) {
        const branches = res.data.branches || [];
        const userRegion = res.data.user_region || "";
        setBranchOptions(branches);
        const matched = branches.find(b => b.toLowerCase() === userRegion.toLowerCase());
        if (matched) setNewLead(prev => ({ ...prev, branch: matched }));
      }
    } catch { console.error('branchFetch error'); }
  };

  useEffect(() => {
    fetchProducts();
    fetchBranches();
  }, []);

  useEffect(() => { setSearchTerm(searchQuery); }, [searchQuery]);

  /* ════════ CATEGORY → PRODUCT DERIVATIONS ════════ */
  const categoryOptions = useMemo(() => {
    const cats = allProducts
      .filter(p => p.status === 'ACTIVE')
      .map(p => p.category)
      .filter(Boolean);
    return [...new Set(cats)];
  }, [allProducts]);

  const productsByCategory = useMemo(() => {
    const map = {};
    allProducts
      .filter(p => p.status === 'ACTIVE')
      .forEach(p => {
        if (!p.category || !p.name) return;
        if (!map[p.category]) map[p.category] = [];
        if (!map[p.category].includes(p.name)) map[p.category].push(p.name);
      });
    return map;
  }, [allProducts]);

  /* used to infer a lead's category from its (comma-separated) product list
     when opening the view/edit modal, since only `product` is persisted */
  const productToCategory = useMemo(() => {
    const map = {};
    allProducts.forEach(p => {
      if (p.name && p.category && !(p.name in map)) map[p.name] = p.category;
    });
    return map;
  }, [allProducts]);

  /* ════════ VALIDATION ════════ */
  const validateLead = (lead) => {
    const errs = {};
    if (!lead.companyName) errs.companyName = true;
    if (!lead.mobileNumber) errs.mobileNumber = true;
    if (!lead.branch) errs.branch = true;
    if (!lead.address) errs.address = true;
    if (!lead.category) errs.category = true;
    if (!lead.productNames || lead.productNames.length === 0) errs.productNames = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ════════ SAVE / UPDATE ════════ */
  const saveLead = async () => {
    if (!validateLead(newLead)) { alert("Required fields are missing."); return; }
    try {
      const res = await fetch(`${SERVER_URL}/DataInsert/insertNewLead.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...newLead, employee }),
      });
      const result = await res.json();
      if (result.status === "success") {
        setNewLead({ ...blankLead });
        alert("Lead Created.");
        setNewLeadModal(false);
        window.location.reload();
      } else { alert(result.message || "Failed to create lead."); }
    } catch { alert("Network error. Please try again."); }
  };

  const updateLead = async (leadData) => {
    /* If the lead is being moved to "Order Confirmed" and doesn't yet have
       a quotation number, a Call Number is required — it becomes the
       quotation number. If a quotation was already generated (quotation
       flow sets quotation_number), we never ask for it again. */
    const needsCallNumber =
      leadData.status === 'Order Confirmed' && !leadData.quotation_number;

    if (needsCallNumber && !callNumberInput.trim()) {
      alert("This lead doesn't have a quotation yet. Please enter a Call Number to proceed.");
      return;
    }

    const payload = {
      ...leadData,
      leadId: leadData.lead_id,
      employee,
      productNames: leadData.productNames || [],
    };
    if (needsCallNumber) payload.callNumber = callNumberInput.trim();

    try {
      const res = await fetch(`${SERVER_URL}/DataUpdate/leadUpdate.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Network error");
      const result = await res.json();
      if (result.status === "success") {
        alert("Lead Updated Successfully.");
        setIsEditing(false);
        setViewLeadModal(false);
        setCallNumberInput("");
        window.location.reload();
      } else if (result.requiresCallNumber) {
        alert(result.message || "Please provide a Call Number.");
      } else { alert(result.message || "Failed to update lead."); }
    } catch { alert("Error updating lead."); }
  };

  /* ════════ PROSPECT CODE → STATUS TEXT ════════ */
  const mapProspectToStatus = (prospect) => {
    switch (String(prospect)) {
      case "0": return "New Lead";
      case "1": return "Estimate Required";
      case "2": return "Estimate Submitted";
      case "3": return "Follow up Running";
      case "4": return "Negotiation";
      case "5": return "Order Confirmed";
      case "6": return "Completed & Review Collected";
      case "7": return "Not Interested";
      case "8": return "Lost";
      default: return "";
    }
  };

  /* ════════ QUOTATION VIEW / DOWNLOAD ════════
     Takes the full lead row (not just the quotation_number) so the
     preview/PDF header can show that lead's branch + billing details —
     matching exactly what QuotationModal renders. */
  const openQuotationPreview = async (lead) => {
    if (!lead?.quotation_number) return;
    setQuotationPreview({
      open: true, loading: true, error: "",
      branch: lead.branch || "",
      customer: {
        companyName: lead.companyName,
        address: lead.address,
        pinCode: lead.pinCode,
      },
      quotationNumber: lead.quotation_number,
      items: [], terms: [], createdBy: "",
    });
    try {
      const res = await axios.post(`${SERVER_URL}/DataFetch/fetchQuotationByNumber.php`, {
        quotation_number: lead.quotation_number,
      });
      if (res.data.status === "success") {
        setQuotationPreview(prev => ({
          ...prev,
          loading: false,
          items: res.data.items || [],
          terms: res.data.terms || [],
          createdBy: res.data.created_by || "",
        }));
      } else {
        setQuotationPreview(prev => ({ ...prev, loading: false, error: res.data.message || "Quotation not found." }));
      }
    } catch {
      setQuotationPreview(prev => ({ ...prev, loading: false, error: "Network error while fetching quotation." }));
    }
  };

  const closeQuotationPreview = () => setQuotationPreview({
    open: false, loading: false, error: "",
    branch: "", customer: null, quotationNumber: "",
    items: [], terms: [], createdBy: "",
  });

  /* Builds the same params shape quotationPdf.js / QuotationDocumentPreview
     expect, from whatever is currently loaded in quotationPreview state. */
  const quotationDocParams = () => ({
    branchName: quotationPreview.branch,
    quotationNumber: quotationPreview.quotationNumber,
    customer: quotationPreview.customer,
    rows: quotationPreview.items.map(it => ({
      category: it.category,
      item: it.item,
      qty: it.qty,
      rate: it.rate,
      gstPct: it.gst,
    })),
    subject: "",
    employeeName: quotationPreview.createdBy,
    employeePhone: "",
    terms: quotationPreview.terms,
  });

  /* ════════ TABLE FILTER OPTIONS ════════ */
  const toOpts = (arr = []) => arr.map(v => ({ label: String(v), value: v }));
  const uniqOpts = (arr) => [...new Set(arr)].map(v => ({ label: String(v), value: v }));

  const employeeNameOptions = useMemo(() => uniqOpts(leads.map(l => l.username).filter(Boolean)), [leads]);
  const companyOptions = useMemo(() => uniqOpts(leads.map(l => l.companyName).filter(Boolean)), [leads]);
  const categoryOptionsFilter = useMemo(() => uniqOpts(leads.map(l => l.product).filter(Boolean)), [leads]);
  const slNoOptions = useMemo(() => uniqOpts(leads.map(l => l.slNo).filter(Boolean)), [leads]);
  const statusOptionsFilter = useMemo(() => {
    const uniqueProspects = [...new Set(
      leads.map(l => l.prospect).filter(v => v !== undefined && v !== null && v !== '')
    )];
    return uniqueProspects.map(v => ({ label: mapProspectToStatus(v), value: v }));
  }, [leads]);
  const leadNumberOptions = useMemo(() => uniqOpts(leads.map(l => l.quotation_number).filter(Boolean)), [leads]);
  const branchOptionsFilter = useMemo(() => {
    const map = new Map();
    leads.forEach(l => {
      if (l.branch) { const k = l.branch.toLowerCase(); if (!map.has(k)) map.set(k, l.branch); }
    });
    return Array.from(map.values()).map(v => ({ label: v, value: v.toLowerCase() }));
  }, [leads]);

  /* ════════ FILTERED LEADS ════════ */
  const filteredLeads = useMemo(() => leads.filter(lead => {
    const q = searchTerm.toLowerCase();
    const matchSearch =
      (lead.companyName?.toLowerCase() || "").includes(q) ||
      (lead.contactPerson?.toLowerCase() || "").includes(q) ||
      (lead.mobileNumber?.toString() || "").includes(q) ||
      (lead.username?.toLowerCase() || "").includes(q);
    const matchEmployee = employeeNameFilter.length === 0 || employeeNameFilter.includes(lead.username);
    const matchBranch = branchFilter.length === 0 || branchFilter.includes((lead.branch || "").toLowerCase());
    const matchCompany = companyFilter.length === 0 || companyFilter.includes(lead.companyName);
    const matchCategory = categoryFilter.length === 0 || categoryFilter.includes(lead.product);
    const matchSlNo = slNoFilter.length === 0 || slNoFilter.includes(lead.slNo);
    const matchStatus = statusFilter.length === 0 || statusFilter.includes(lead.prospect);
    const matchLeadNumber = leadNumberFilter.length === 0 || leadNumberFilter.includes(lead.quotation_number);
    return matchSearch && matchEmployee && matchBranch && matchCompany &&
      matchCategory && matchSlNo && matchStatus && matchLeadNumber;
  }), [leads, searchTerm, employeeNameFilter, branchFilter, companyFilter,
    categoryFilter, slNoFilter, statusFilter, leadNumberFilter]);

  useEffect(() => { onFilteredDataChange(filteredLeads); }, [filteredLeads]);

  /* ════════ ROW CLICK ════════ */
  const handleRowClick = (lead) => {
    const productList = (lead.product || "")
      .split(",")
      .map(s => s.trim())
      .filter(Boolean);
    const inferredCategory = productList.length > 0
      ? (productToCategory[productList[0]] || "")
      : "";
    setSelectedLead({
      ...lead,
      category: inferredCategory,
      productNames: productList,
      status: mapProspectToStatus(String(lead.prospect)),
    });
    setViewLeadModal(true);
    setIsEditing(false);
    setCallNumberInput("");
  };

  const handleNewLeadChange = (e) => {
    const { name, value } = e.target;
    setNewLead(prev => {
      if (name === 'category' && prev.category !== value) {
        return { ...prev, category: value, productNames: [] };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setSelectedLead(prev => {
      if (name === 'category' && prev.category !== value) {
        return { ...prev, category: value, productNames: [] };
      }
      return {
        ...prev,
        [name]: name === "branch" ? value.toLowerCase() : value,
      };
    });
  };

  /* ════════ DROPDOWN OPTIONS PER FIELD ════════ */
  const getFieldOptions = (field) => {
    switch (field.options) {
      case 'categories': return toOpts(categoryOptions);
      case 'branches': return branchOptions.map(b => ({ label: b, value: b.toLowerCase() }));
      case 'referenceOpts': return toOpts(REFERENCE_OPTIONS);
      case 'statusOptions': return toOpts(STATUS_OPTIONS);
      default:
        if (Array.isArray(field.options)) return toOpts(field.options);
        return [];
    }
  };

  /* ════════ RENDER ONE FIELD ════════ */
  const nonEditableFields = ["companyName", "leadCreatedDate", "nextFollowUp"];

  const renderFormField = (field, value, onChange, disabled = false) => {
    const fieldValue = value ?? "";
    const hasError = errors[field.name];
    const isDisabled = disabled || field.readOnly;

    if (field.type === "textarea") {
      return (
        <textarea
          className={
            "w-full bg-slate-50 border rounded-lg px-3 py-2 text-sm text-slate-700 " +
            "placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 " +
            "transition-colors resize-none disabled:opacity-50 disabled:bg-slate-100 " +
            (hasError ? "border-rose-400" : "border-slate-200")
          }
          rows={3}
          placeholder={field.placeholder}
          name={field.name}
          value={fieldValue}
          onChange={onChange}
          disabled={isDisabled}
        />
      );
    }

    if (field.type === "select" || field.options) {
      return (
        <>
          <SearchableDropdown
            label=""
            options={getFieldOptions(field)}
            value={fieldValue}
            placeholder={field.placeholder}
            disabled={isDisabled}
            onChange={(val) => onChange({ target: { name: field.name, value: val } })}
          />
          {hasError && <p className="text-rose-500 text-xs mt-1">Required field</p>}
        </>
      );
    }

    return (
      <>
        <input
          className={`${inputStyle} ${hasError ? "border-rose-400 focus:ring-rose-400" : ""}`}
          type={field.type}
          placeholder={field.placeholder}
          name={field.name}
          value={fieldValue}
          onChange={onChange}
          disabled={isDisabled}
          readOnly={field.readOnly}
        />
        {hasError && <p className="text-rose-500 text-xs mt-1">Required field</p>}
      </>
    );
  };

  /* Product Description multi-select is rendered separately from
     renderFormField because its options depend on the sibling Category
     value of whichever lead object (newLead / selectedLead) it belongs to. */
  const renderProductNamesField = (category, values, onChangeArr, disabled) => {
    const opts = (productsByCategory[category] || []).map(name => ({ label: name, value: name }));
    const hasError = errors.productNames;
    return (
      <>
        <MultiSelectDropdown
          options={opts}
          values={Array.isArray(values) ? values : []}
          onChange={onChangeArr}
          placeholder={category ? "Select Product(s)" : "Select a category first"}
          disabled={disabled || !category}
        />
        {hasError && <p className="text-rose-500 text-xs mt-1">Required field</p>}
      </>
    );
  };

  const startEntry = filteredLeads.length > 0 ? (currentPage - 1) * limit + 1 : 0;
  const endEntry = Math.min(currentPage * limit, filteredLeads.length);
  const totalPagesCalc = Math.ceil(filteredLeads.length / limit);

  const showCallNumberField =
    isEditing && selectedLead &&
    selectedLead.status === 'Order Confirmed' &&
    !selectedLead.quotation_number;

  /* ══════════════════════════════════ JSX ══════════════════════════════════ */
  return (
    <>
      <style>{`
        .att-scrollbar::-webkit-scrollbar        { width:5px; height:5px; }
        .att-scrollbar::-webkit-scrollbar-track  { background:#f1f5f9; border-radius:10px; }
        .att-scrollbar::-webkit-scrollbar-thumb  { background:#cbd5e1; border-radius:10px; }
        .att-scrollbar::-webkit-scrollbar-thumb:hover { background:#94a3b8; }
      `}</style>

      <div className="flex flex-col h-screen" style={{ background: "#f0f4f8" }}>

        {/* ── STICKY HEADER ─────────────────────────────────────────── */}
        <div
          className="sticky top-0 z-20 border-b border-slate-200 px-6 py-3 bg-white"
          style={{ boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-1 h-6 rounded-full"
                style={{ background: "linear-gradient(180deg,#1e40af,#0369a1)" }} />
              <h2 className="text-lg font-bold tracking-tight text-slate-800">Leads Activity</h2>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Search */}
              <div className="flex w-full sm:w-[300px]">
                <input
                  type="text"
                  placeholder="Search by name, phone or status..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-l-lg bg-slate-50 border border-slate-200 px-4 py-2 text-sm
                             text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <button
                  className="px-3 rounded-r-lg text-white"
                  style={{ background: "linear-gradient(135deg,#1e40af,#0369a1)" }}
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Show entries */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Show</label>
                <select
                  value={limit}
                  onChange={(e) => onLimitChange(Number(e.target.value))}
                  className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm
                             text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="text-xs text-slate-500">entries</span>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setNewLeadModal(true)}
                  className="flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90"
                  style={{ background: "linear-gradient(135deg,#1e40af,#0369a1)", boxShadow: "0 2px 8px rgba(30,64,175,0.18)" }}
                >
                  <PlusIcon className="h-2 w-2" />Lead
                </button>
                <button
                  onClick={() => setFollowUpModalOpen(true)}
                  className="flex items-center gap-0.5 px-2 py-2 rounded-lg text-sm font-bold
                             text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100"
                >
                  Follow Up
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── TABLE ─────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden p-2.5">
          <div
            className="rounded-xl overflow-hidden h-full flex flex-col bg-white border border-slate-200"
            style={{ boxShadow: "0 1px 1px rgba(15,23,42,0.06)" }}
          >
            <div className="flex-1 overflow-y-auto overflow-x-auto att-scrollbar">
              <table className="min-w-[1150px] w-full text-sm text-left border-collapse">

                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                  <tr>
                    {[
                      { filter: <TableHeaderFilter label="Sl. No." options={slNoOptions} selectedValues={slNoFilter} onChange={setSlNoFilter} /> },
                      { filter: <TableHeaderFilter label="Lead Number" options={leadNumberOptions} selectedValues={leadNumberFilter} onChange={setLeadNumberFilter} /> },
                      { filter: <TableHeaderFilter label="Employee" options={employeeNameOptions} selectedValues={employeeNameFilter} onChange={setEmployeeNameFilter} /> },
                      { filter: <TableHeaderFilter label="Branch" options={branchOptionsFilter} selectedValues={branchFilter} onChange={setBranchFilter} /> },
                      { filter: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</span> },
                      { filter: <TableHeaderFilter label="Company Name" options={companyOptions} selectedValues={companyFilter} onChange={setCompanyFilter} /> },
                      { filter: <TableHeaderFilter label="Product" options={categoryOptionsFilter} selectedValues={categoryFilter} onChange={setCategoryFilter} /> },
                      { filter: <TableHeaderFilter label="Status" options={statusOptionsFilter} selectedValues={statusFilter} onChange={setStatusFilter} /> },
                      { filter: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Quotation</span> },
                      { filter: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Next Follow Up</span> },
                    ].map((col, i) => (
                      <th key={i} className="py-3 px-4 text-left border-b border-slate-200">
                        {col.filter}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {filteredLeads.length ? (
                    filteredLeads
                      .slice((currentPage - 1) * limit, currentPage * limit)
                      .map((lead, idx) => (
                        <tr
                          key={lead.lead_id ?? idx}
                          className="border-t border-slate-100 cursor-pointer hover:bg-blue-50/60 transition-colors"
                          onClick={() => handleRowClick(lead)}
                        >
                          <td className="py-3 px-4 text-slate-500 text-xs truncate max-w-[100px]">
                            {lead.slNo || '—'}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-xs truncate max-w-[120px]">
                            {lead.quotation_number || '—'}
                          </td>
                          <td className="py-3 px-4 text-slate-800 text-xs font-medium whitespace-nowrap">
                            {lead.username}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                            {lead.branch}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                            {lead.leadCreatedDate}
                          </td>
                          <td className="py-3 px-4 text-slate-700 text-xs truncate max-w-[200px]">
                            {lead.companyName}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-xs truncate max-w-[220px]">
                            {lead.product}
                          </td>
                          <td className="py-3 px-4 text-xs">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                              (mapProspectToStatus(String(lead.prospect)) || "").toLowerCase().includes("completed")
                                ? "bg-emerald-100 text-emerald-700"
                                : (mapProspectToStatus(String(lead.prospect)) || "").toLowerCase().includes("lost") ||
                                  (mapProspectToStatus(String(lead.prospect)) || "").toLowerCase().includes("not interested")
                                  ? "bg-rose-100 text-rose-700"
                                  : "bg-amber-100 text-amber-700"
                            }`}>
                              {mapProspectToStatus(String(lead.prospect))}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-xs">
                            {lead.quotation_number ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); openQuotationPreview(lead); }}
                                className="flex items-center gap-1 text-blue-600 font-semibold hover:underline"
                              >
                                <DocumentTextIcon className="w-3.5 h-3.5" /> View
                              </button>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-slate-400 text-xs whitespace-nowrap">
                            {lead.nextFollowUp}
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="py-12 text-center text-slate-400 text-sm">
                        No leads found with current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── PAGINATION ────────────────────────────────────────────── */}
        <div
          className="sticky bottom-0 z-10 border-t border-slate-200 px-3 py-1.5 bg-white"
          style={{ boxShadow: "0 -1px 4px rgba(15,23,42,0.06)" }}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="text-xs text-slate-500 font-medium">
              Showing <span className="text-slate-700 font-bold">{startEntry}</span> to{' '}
              <span className="text-slate-700 font-bold">{endEntry}</span> of{' '}
              <span className="text-slate-700 font-bold">{filteredLeads.length}</span> entries
            </div>
            <div className="inline-flex items-center gap-2">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100
                           text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="w-2 h-2" />
              </button>
              <div className="px-2 py-1 rounded-lg border border-slate-200 bg-slate-50
                              text-xs text-slate-600 font-semibold">
                Page {currentPage}/{totalPagesCalc || 1}
              </div>
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPagesCalc}
                className="p-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100
                           text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="w-2 h-2" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          ADD NEW LEAD MODAL
      ════════════════════════════════════════════════════════ */}
      {newLeadModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}
        >
          <div
            className="w-full max-w-3xl rounded-2xl bg-white border border-slate-200 p-6
                       overflow-y-auto max-h-[90vh] att-scrollbar"
            style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
          >
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full"
                  style={{ background: "linear-gradient(180deg,#1e40af,#0369a1)" }} />
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <PlusIcon className="h-5 w-5 text-blue-600" /> Add New Lead
                </h3>
              </div>
              <button onClick={() => setNewLeadModal(false)} className="text-slate-400 hover:text-slate-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {formFields.map((field) => (
                <div key={field.name} className={field.colSpan === 2 ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    {field.label}
                    {field.required && <span className="text-rose-500 ml-1">*</span>}
                  </label>
                  {field.name === 'productNames'
                    ? renderProductNamesField(
                        newLead.category,
                        newLead.productNames,
                        (arr) => setNewLead(prev => ({ ...prev, productNames: arr })),
                        false,
                      )
                    : renderFormField(field, newLead[field.name], handleNewLeadChange, false)}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setNewLeadModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600
                           border border-slate-200 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={saveLead}
                className="px-5 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90"
                style={{ background: "linear-gradient(135deg,#1e40af,#0369a1)", boxShadow: "0 2px 8px rgba(30,64,175,0.18)" }}
              >
                Save Lead
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════
          VIEW / EDIT LEAD MODAL
      ════════════════════════════════════════════════════════ */}
      {viewLeadModal && selectedLead && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}
        >
          <div
            className="w-full max-w-3xl rounded-2xl bg-white border border-slate-200 p-6
                       overflow-y-auto max-h-[90vh] att-scrollbar"
            style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
          >
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full"
                  style={{ background: "linear-gradient(180deg,#1e40af,#0369a1)" }} />
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  {isEditing
                    ? <><PencilIcon className="h-5 w-5 text-blue-600" /> Edit Lead</>
                    : 'Lead Details'}
                </h3>
              </div>
              <div className="flex gap-2">
                {selectedLead.quotation_number && (
                  <button
                    onClick={() => openQuotationPreview(selectedLead)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold
                               text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
                  >
                    <DocumentTextIcon className="w-4 h-4" /> View Quotation
                  </button>
                )}
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold
                               text-white hover:opacity-90"
                    style={{ background: "linear-gradient(135deg,#1e40af,#0369a1)", boxShadow: "0 2px 8px rgba(30,64,175,0.18)" }}
                  >
                    <PencilIcon className="w-4 h-4" /> Edit
                  </button>
                )}
                <button onClick={() => setViewLeadModal(false)} className="text-slate-400 hover:text-slate-600">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {formFields.map((field) => (
                <div key={field.name} className={field.colSpan === 2 ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    {field.label}
                    {field.required && <span className="text-rose-500 ml-1">*</span>}
                  </label>
                  {field.name === 'productNames'
                    ? renderProductNamesField(
                        selectedLead.category,
                        selectedLead.productNames,
                        (arr) => setSelectedLead(prev => ({ ...prev, productNames: arr })),
                        !isEditing,
                      )
                    : renderFormField(
                        field,
                        selectedLead[field.name],
                        handleEditChange,
                        !isEditing || nonEditableFields.includes(field.name)
                      )}
                </div>
              ))}

              {/* Call Number prompt — only shown while editing, moving to
                  Order Confirmed, and no quotation number exists yet */}
              {showCallNumberField && (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-amber-600 uppercase tracking-wider mb-1">
                    Call Number (becomes Quotation Number) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    className={inputStyle}
                    placeholder="Enter Call Number"
                    value={callNumberInput}
                    onChange={(e) => setCallNumberInput(e.target.value)}
                  />
                  <p className="text-xs text-amber-600 mt-1">
                    No quotation has been generated for this lead yet. This Call Number will be saved as the Quotation Number.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setViewLeadModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600
                           border border-slate-200 hover:bg-slate-50"
              >
                Close
              </button>
              {isEditing && (
                <button
                  onClick={() => updateLead(selectedLead)}
                  className="px-5 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90"
                  style={{ background: "linear-gradient(135deg,#059669,#10b981)", boxShadow: "0 2px 8px rgba(5,150,105,0.18)" }}
                >
                  Save Changes
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════
          QUOTATION PREVIEW / DOWNLOAD MODAL
          Renders the exact same document component QuotationModal uses,
          so the format is identical wherever a quotation is viewed.
      ════════════════════════════════════════════════════════ */}
      {quotationPreview.open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: "rgba(15,23,42,0.72)", backdropFilter: "blur(6px)" }}
        >
          <div
            className="w-[680px] max-h-[88vh] rounded-xl flex flex-col bg-white border border-slate-200"
            style={{ boxShadow: "0 24px 80px rgba(15,23,42,0.28)" }}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                <div>
                  <h3 className="text-base font-bold m-0 text-slate-800">Quotation</h3>
                  {quotationPreview.quotationNumber && (
                    <p className="text-xs text-blue-500 m-0 mt-0.5">#{quotationPreview.quotationNumber}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!quotationPreview.loading && !quotationPreview.error && (
                  <button
                    type="button"
                    onClick={() => buildQuotationPDF({ ...quotationDocParams(), forEmail: false })}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                  >
                    <ArrowDownTrayIcon className="h-3.5 w-3.5" /> Download
                  </button>
                )}
                <button type="button" onClick={closeQuotationPreview}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6" style={{ background: "#f0f4f8" }}>
              {quotationPreview.loading && (
                <p className="text-sm text-slate-400 py-8 text-center">Loading quotation…</p>
              )}
              {!quotationPreview.loading && quotationPreview.error && (
                <p className="text-sm text-rose-500 py-8 text-center">{quotationPreview.error}</p>
              )}
              {!quotationPreview.loading && !quotationPreview.error && (
                <QuotationDocumentPreview {...quotationDocParams()} />
              )}
            </div>
          </div>
        </div>
      )}

      {followUpModalOpen && (
        <FollowUpModal
          isOpen={followUpModalOpen}
          onClose={() => setFollowUpModalOpen(false)}
          onRefresh={() => window.location.reload()}
        />
      )}
    </>
  );
};

export default LeadTable;